import { useEffect, useRef, useState, type FormEvent } from "react";
import { useLocation } from "react-router-dom";
import type { ChatReply } from "@shared/modules/x-assistant";
import { api, ApiError } from "../../../core/lib/api";
import { useAuthStore } from "../../../core/stores/auth";
import { Button } from "../../../core/components/ui/button";
import { Icon } from "../../../core/components/ui/icon";

const money = (cents: number) =>
  new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD" }).format(cents / 100);
const date = (value: string) =>
  new Date(value).toLocaleString("fr-CA", {
    timeZone: "America/Toronto",
    weekday: "long",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
function errorText(err: unknown) {
  const code = err instanceof ApiError ? err.code : "";
  return (
    (
      {
        rate_limited: "Quelques instants de pause, puis vous pourrez réessayer.",
        conversation_expired: "La conversation a expiré. Démarrez une nouvelle conversation.",
        conversation_limit: "Démarrez une nouvelle conversation pour continuer.",
        quote_changed: "Le tarif a changé. Demandez un nouveau récapitulatif avant de confirmer.",
        slot_unavailable: "Ce créneau n’est plus libre. Demandez un autre horaire.",
        proposal_expired: "Ce récapitulatif a expiré. Demandez-en un nouveau.",
        already_booked:
          "Ce rendez-vous est déjà enregistré. Démarrez une nouvelle conversation pour une autre demande.",
      } as Record<string, string>
    )[code] ??
    "L’assistant est momentanément indisponible. Si vous veniez de confirmer un rendez-vous, vérifiez l’agenda avant de recommencer."
  );
}

export function AssistantChat({ onClose }: { onClose?: () => void }) {
  const user = useAuthStore((s) => s.user);
  const [data, setData] = useState<ChatReply | null>(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const feed = useRef<HTMLDivElement>(null);
  const text = useRef<HTMLTextAreaElement>(null);
  const key = `protecto-admin-chat:${user?.id}`;
  useEffect(() => {
    let disposed = false;
    let id: string | null = null;
    try {
      id = sessionStorage.getItem(key);
    } catch {
      /* stockage facultatif */
    }
    if (id) {
      setBusy(true);
      api<ChatReply>("/api/assistant/admin/session", {
        method: "POST",
        body: JSON.stringify({ conversationId: id }),
      })
        .then((reply) => {
          if (!disposed) setData(reply);
        })
        .catch(() => {
          try {
            sessionStorage.removeItem(key);
          } catch {
            /* facultatif */
          }
        })
        .finally(() => {
          if (!disposed) setBusy(false);
        });
    }
    text.current?.focus();
    return () => {
      disposed = true;
    };
  }, [key]);
  useEffect(() => {
    feed.current?.scrollTo({ top: feed.current.scrollHeight, behavior: "smooth" });
  }, [data, busy, error]);
  const save = (reply: ChatReply) => {
    setData(reply);
    try {
      sessionStorage.setItem(key, reply.conversationId);
    } catch {
      /* facultatif */
    }
  };
  async function send(message: string) {
    if (!message.trim() || busy || data?.booking) return;
    setBusy(true);
    setError("");
    try {
      save(
        await api<ChatReply>("/api/assistant/admin/message", {
          method: "POST",
          body: JSON.stringify({ conversationId: data?.conversationId, message: message.trim() }),
        }),
      );
      setInput("");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
      text.current?.focus();
    }
  }
  function submit(event: FormEvent) {
    event.preventDefault();
    void send(input);
  }
  async function confirm() {
    if (!data?.proposal || busy) return;
    setBusy(true);
    setError("");
    try {
      save(
        await api<ChatReply>("/api/assistant/admin/confirm", {
          method: "POST",
          body: JSON.stringify({
            conversationId: data.conversationId,
            proposalId: data.proposal.id,
            confirmed: true,
          }),
        }),
      );
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }
  function reset() {
    setData(null);
    setError("");
    setInput("");
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* facultatif */
    }
    text.current?.focus();
  }
  const proposal = data?.proposal;
  return (
    <section className="assistant-chat" aria-label="Assistant administrateur Léo">
      <header className="assistant-chat__header">
        <span className="assistant-chat__avatar">
          <Icon name="sparkles" size={22} />
        </span>
        <div>
          <strong>
            Léo <small>Assistant admin</small>
          </strong>
          <p>Vos données, à portée de conversation.</p>
        </div>
        {onClose && (
          <button
            type="button"
            className="assistant-chat__close"
            onClick={onClose}
            aria-label="Fermer l’assistant"
          >
            <Icon name="close" />
          </button>
        )}
      </header>
      <div className="assistant-chat__scope">
        <Icon name="shield" size={15} /> Consultation et ajout de rendez-vous uniquement
      </div>
      <div
        className="assistant-chat__feed"
        ref={feed}
        role="log"
        aria-live="polite"
        aria-relevant="additions text"
      >
        {!data?.messages.length && (
          <div className="assistant-chat__welcome">
            <h2>Un coup de main ?</h2>
            <p>
              Retrouvez un client, consultez l’agenda ou préparez un rendez-vous. Je vous demanderai
              confirmation avant de l’ajouter.
            </p>
            <div className="assistant-chat__suggestions">
              {[
                "Quels rendez-vous avons-nous demain ?",
                "Quels services antirouille proposons-nous ?",
                "Ajouter un rendez-vous",
              ].map((m) => (
                <button key={m} disabled={busy} onClick={() => void send(m)}>
                  {m}
                  <Icon name="chevron-right" size={16} />
                </button>
              ))}
            </div>
          </div>
        )}
        {data?.messages.map((m, i) => (
          <div key={i} className={`assistant-chat__message assistant-chat__message--${m.role}`}>
            <small>{m.role === "user" ? "Vous" : "Léo"}</small>
            <p>{m.text}</p>
          </div>
        ))}
        {proposal && !data.booking && (
          <div className="assistant-chat__recap">
            <strong>Vérifier le rendez-vous</strong>
            <p>{proposal.services.join(" · ")}</p>
            <dl>
              <dt>Quand</dt>
              <dd>{date(proposal.startAt)} · heure du Québec</dd>
              <dt>Client</dt>
              <dd>
                {proposal.clientName} · {proposal.phone}
              </dd>
              <dt>Véhicule</dt>
              <dd>{proposal.vehicle}</dd>
              <dt>Total avec taxes</dt>
              <dd>{money(proposal.totalWithTaxCents)}</dd>
            </dl>
            <p>Paiement à l’atelier. Le créneau sera revérifié à la confirmation.</p>
            <Button onClick={() => void confirm()} disabled={busy}>
              Confirmer le rendez-vous
            </Button>
          </div>
        )}
        {data?.booking && (
          <div className="assistant-chat__success">
            <Icon name="check-circle" /> Rendez-vous enregistré · {date(data.booking.startAt)}
            <a href={`/travaux/${encodeURIComponent(data.booking.reference)}`}>
              Ouvrir le rendez-vous
            </a>
          </div>
        )}
        {busy && (
          <p className="assistant-chat__waiting" role="status">
            Léo consulte les informations…
          </p>
        )}
        {error && (
          <p className="assistant-chat__error" role="alert">
            {error}
          </p>
        )}
      </div>
      <form className="assistant-chat__form" onSubmit={submit}>
        <label className="assistant-chat__label" htmlFor="admin-chat-message">
          Votre message
        </label>
        <textarea
          id="admin-chat-message"
          ref={text}
          rows={2}
          maxLength={1500}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={busy || !!data?.booking}
          placeholder="Posez votre question à Léo…"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              void send(input);
            }
          }}
        />
        <Button
          type="submit"
          disabled={busy || !input.trim() || !!data?.booking}
          aria-label="Envoyer le message"
        >
          <Icon name="arrow-up" />
        </Button>
      </form>
      <footer className="assistant-chat__footer">
        <span>Assistant IA · Vérifiez le récapitulatif.</span>
        <button onClick={reset} disabled={busy}>
          Nouvelle conversation
        </button>
      </footer>
    </section>
  );
}
export function AssistantLauncher() {
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const close = () => {
    setOpen(false);
    trigger.current?.focus();
  };
  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", listener);
    return () => document.removeEventListener("keydown", listener);
  }, []);
  if (user?.role !== "admin" || location.pathname === "/assistant") return null;
  return (
    <div className="assistant-launcher">
      {open && (
        <div className="assistant-launcher__panel" role="dialog" aria-label="Discuter avec Léo">
          <AssistantChat onClose={close} />
        </div>
      )}
      <button
        ref={trigger}
        className="assistant-launcher__button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name={open ? "close" : "sparkles"} />
        {open ? "Fermer" : "Demander à Léo"}
      </button>
    </div>
  );
}
